import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {animate, state, style, transition, trigger} from "@angular/animations";
import {Subject, Subscription, first, interval, take, takeUntil} from "rxjs";
import {INdaResponse, INdaResponseItem, NdaType} from "../../models";
import {MatTableDataSource} from "@angular/material/table";
import {
  Config,
  getConfig,
  initialConfigState,
  initialThemeState,
  Theme,
  toggleLoading, updateDeal
} from "../../../redux";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort, Sort} from "@angular/material/sort";
import {Store} from "@ngrx/store";
import {ToastrService} from "ngx-toastr";
import {DealNdaService, DealService} from "../../services";
import {MatDialog} from "@angular/material/dialog";
import {MnpiModalComponent} from "./mnpi-modal/mnpi-modal.component";
import {ActivatedRoute, Router} from "@angular/router";
import {NegotiateModalComponent} from "./negotiate-modal/negotiate-modal.component";
import {ModalSize, UtilService} from "../../../shared/services/util/util.service";
import { LookupService } from 'src/app/services';
import { HttpErrorResponse } from '@angular/common/http';
import { DataService } from 'src/app/services/data/data.service';

@Component({
  selector: 'app-deal-nda',
  templateUrl: './deal-nda.component.html',
  styleUrls: ['./deal-nda.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class DealNdaComponent implements OnInit, OnDestroy, AfterViewInit {
  config$: Subscription = new Subscription();
  selectedBusinessUnit$: Subscription = new Subscription();
  document$: Subscription = new Subscription();

  config: Config = initialConfigState;

  dealId: string;
  NdaType = NdaType;
  nda: INdaResponse = {} as unknown as INdaResponse;
  ndaStatus: "PENDING" | "SENT" | "COMPLETED" | "DECLINED" = "PENDING";
  dataSource: MatTableDataSource<INdaResponseItem> = new MatTableDataSource();
  columnsToDisplay = ['businessUnit', 'signatory', 'type', 'status', 'created', 'actions'];
  intervalSub!: Subscription;

  @ViewChild(MatPaginator) paginator?: MatPaginator;
  @ViewChild(MatSort) sort?: MatSort;

  loading: boolean = false; 

  constructor(private store: Store,
              private toastr: ToastrService,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              public lookupService: LookupService,
              private dealService: DealService,
              private dataService: DataService,
              private dealNdaService: DealNdaService,
              public dialog: MatDialog,
              public util: UtilService
  ) {

    // this.config$ = this.store.select(getConfig).subscribe((config: Config) => {
    //   this.config = config;
    // });

    this.dealId = this.dataService.getDealId();
    //  this.activatedRoute.parent?.snapshot.paramMap.get("id") || '';
    this.getDealNda();
  }
  ngOnInit() {
  }

  ngAfterViewInit() {
    // get params
    let envelopeId: string = this.activatedRoute.snapshot.queryParamMap.get('envelopeId') || '';
    let event: string = this.activatedRoute.snapshot.queryParamMap.get('event') || '';
    console.log("Get NDA by EnvelopeId", envelopeId, event);

    if (!!envelopeId && !!event) {
      // there are values to these query params, so search for the NDA
      this.intervalSub = interval(1000).pipe(take(120)).subscribe({
        next: () => {
          console.log("Interval elapsed!");
          this.store.dispatch(toggleLoading({ loading: true, message: "We're processing your NDA status with DocuSign! This could take up to 30 seconds, please wait..." }));
          
          this.dealNdaService.getNdaStatusByEnvelopeId(envelopeId).subscribe({
            next: (res: { isSigned: boolean, status: "PENDING" | "SENT" | "COMPLETED" | "DECLINED" }) => {
              console.log("Get NDA by EnvelopeId result", res);

              // set NDA status
              this.ndaStatus = res.status;

              if (res.status == 'COMPLETED' || res.status == 'DECLINED') {
                this.intervalSub?.unsubscribe();
                this.store.dispatch(toggleLoading({ loading: false }));

                if (res.status == 'COMPLETED') {
                  this.toastr.success("NDA Completed! Refreshing deal page...");
                  this.router.navigate([], {
                    queryParams: {},
                    replaceUrl: true,
                  }).then(() => {
                    // Reload the page after query params are cleared
                    window.location.reload();
                  });
                }
                else if (res.status == 'DECLINED') {
                  this.toastr.error("NDA Declined!");
                }

                this.getDealNda();
              }

            },
            error: (err:  HttpErrorResponse) => {
              console.error(err);
              this.intervalSub?.unsubscribe();
              this.store.dispatch(toggleLoading({ loading: false }));

              if (err.status == 404) {
                this.toastr.error("Enveloped Id for NDA not found!");
              }
            }
          });
        },
        complete: () => {
          this.intervalSub?.unsubscribe();
          this.store.dispatch(toggleLoading({ loading: false }));
          this.toastr.error("Could not get updates on this NDA.  Please refresh your page to restart the process.");
        }
      });
     }
  }

 
  ngOnDestroy() {
    this.intervalSub?.unsubscribe();
  }

  isStatus(status: string) {
    return this.ndaStatus == status;
  }

  /**
   * Get the deal nda
   */
  private getDealNda() {
    console.log("Fetching Deal NDA, for deal Id", this.dealId);

    if (!!this.dealId) {
      this.dealNdaService.getNdaByDealId(this.dealId).subscribe({
        next: (nda: INdaResponse) => {
          console.log("NDA response", nda)
          this.nda = nda;
          this.dataSource = new MatTableDataSource(nda.ndaItems);
          this.initDataSource();
          if (this.nda.showMNPIRequirement) {
            this.openMnpiModal();
          }
        }, error: (err: any) => {
          this.toastr.error("Something went wrong!");
        }
      });
    }
  }

  private initDataSource() {
    this.dataSource.filterPredicate = (data: INdaResponseItem, filter: string) => {
      const fullName = data?.signatory?.firstName + ' ' + data?.signatory?.lastName;
      return data.businessUnit?.displayName?.toLowerCase().includes(filter.toLowerCase()) ||
        fullName.toLowerCase().includes(filter.toLowerCase()) ||
        data.signatory?.email?.toLowerCase().includes(filter.toLowerCase());
    };
    if (!!this.paginator) {
      this.paginator.pageSize = 100;
      this.dataSource.paginator = this.paginator;
    }
    if (!!this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  download(envelopId: string) {
    // download
    this.store.dispatch(toggleLoading({loading: true}));
    this.dealNdaService.downloadNdaByEnvelopeId(envelopId).subscribe({
      next: (res: Blob) => {
        let a = document.createElement('a');
        a.download = "NDA Download.pdf";
        a.href = window.URL.createObjectURL(res);
        a.click();
        this.store.dispatch(toggleLoading({loading: false}));
        this.toastr.success("NDA Downloaded!", $localize`:@@companyName:Rondeivu`);
      },
      error: (err) => {
        this.store.dispatch(toggleLoading({loading: false}));
        this.toastr.error("Could not download file");
      }
    })
  }

  negotiate(type: NdaType) {
    // get HR form of the nda type
    let t = '';
    switch (type) {
      case NdaType.INVESTOR_DEAL_BY_DEAL_NDA:
        t = "Deal-by-Deal";
        break;
      case NdaType.INVESTOR_GLOBAL_NDA:
        t = "Global";
        break;
    }

    this.dialog.open(NegotiateModalComponent, {
      height: 'auto',
      width: this.util.getModalWidth(ModalSize.MEDIUM),
      disableClose: true,
      data: {
        ndaType: t
      }
    })
    .afterClosed().subscribe(result => {
      console.log("Dialog closed!");
      if (result) {
        console.log("Negotiate Deal NDA...");
        this.store.dispatch(toggleLoading({loading: true}));
        this.dealNdaService.negotiateNda(type, this.dealId).subscribe({
          next: (nda: any) => {
            this.store.dispatch(toggleLoading({loading: false}));
            this.toastr.success("Negotiation Started! Please check your email for the link.", $localize`:@@companyName:Rondeivu`);
          }, error: (err: any) => {
            this.store.dispatch(toggleLoading({loading: false}));
            this.toastr.error("Unable to start negotiation! Please contact admin.", $localize`:@@companyName:Rondeivu`);
          }
        });
      }
    });
  }

  viewNdaTemplate(type: NdaType) {
    // const url = this.router.serializeUrl(
    //   this.router.createUrlTree([window.location.origin, this.dataService.getBuSlug(), 'documents', type.toString()], {queryParams: {'dealId': this.dealId}})
    // );
    var redirectUrl = `${window.location.origin}/${this.dataService.getBuSlug()}/documents/${type.toString()}?dealId=${this.dealId}`;
    window.open(redirectUrl, '_blank');
  }


  /**
   * Creates a NDA based on the type
   * @param type
   */
  createNda(type: NdaType) {
    console.log("Creating Deal NDA...");
    this.store.dispatch(toggleLoading({loading: true, message: "Creating NDA with DocuSign, please wait..."}));
    this.dealNdaService.createNda({ndaType: type.toString(), dealId: this.dealId}).subscribe({
      next: (nda: any) => {
        if (!!nda) {
          window.open(nda.url, '_self')?.focus();
        }
        this.store.dispatch(toggleLoading({loading: false}));
        this.toastr.success("NDA Created!", $localize`:@@companyName:Rondeivu`);
      }, error: (err: any) => {
        this.store.dispatch(toggleLoading({loading: false}));
        this.toastr.error("Something went wrong!");
      }
    });
  }

  private openMnpiModal() {
    const mnpiRef = this.dialog.open(MnpiModalComponent, {
      height: 'auto',
      width: this.util.getModalWidth(ModalSize.MEDIUM),
      disableClose: true
    });

    mnpiRef.afterClosed().subscribe(result => {
      console.log("Dialog closed!");
      if (result) {
        console.log("Checking Deal MNPI...");
        this.store.dispatch(toggleLoading({loading: true}));
        this.dealService.checkMnpi().subscribe({
          next: (res: any) => {
            // this.fetchDealAndDispatch();
            this.store.dispatch(toggleLoading({loading: false}));
            this.toastr.success("MNPI Checked!", $localize`:@@companyName:Rondeivu`);
          }, error: (err: any) => {
            this.store.dispatch(toggleLoading({loading: false}));
            this.toastr.error("Something went wrong!");
          }
        });
      }
    });
  }

  //sort
  // sortData(sort: Sort) {
  //   const data = this.ndaList.slice();
  //   if (!sort.active || sort.direction === '') {
  //     this.sortedNdas = data;
  //     return;
  //   }

  //   this.sortedNdas = data.sort((a: INdaResponseItem, b: INdaResponseItem) => {
  //     const isAsc = sort.direction === 'asc';
  //     switch (sort.active) {
  //       case 'company':
  //         return compare(a.businessUnit.displayName, b.businessUnit.displayName, isAsc);
  //       case 'employee':
  //         const n1 = a.signatory.firstName + ' ' + a.signatory.lastName;
  //         const n2 = b.signatory.firstName + ' ' + b.signatory.lastName;
  //         return compare(n1, n2, isAsc);
  //       case 'type':
  //         return compare(this.getTypeText(a.type), this.getTypeText(b.type), isAsc);
  //       case 'status':
  //         return compare(a.status, b.status, isAsc);
  //       case 'created':
  //         return compare(a.created, b.created, isAsc);
  //       default:
  //         return 0;
  //     }
  //   });

  //   this.dataSource = new MatTableDataSource(this.sortedNdas);
  //   // this.initDataSource();
  // }
}

function compare(a: number | string, b: number | string, isAsc: boolean) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}

